import { useCallback, useEffect, useMemo, useRef } from "react";
import pusherService from "./pusherService";

/**
 * Registers temporary listeners for the download flow.
 * Channel is already subscribed on login via PusherConnectionProvider.
 * - startSession: no-op (channel active from login)
 * - endSession: removes download listeners only (channel stays until logout)
 */
export function useDownloadRealtimeSession({ accountId, userId } = {}) {
  const waitingCleanupRef = useRef(null);

  const stopWaiting = useCallback(() => {
    waitingCleanupRef.current?.();
    waitingCleanupRef.current = null;
  }, []);

  const startSession = useCallback(() => {
    if (accountId == null || userId == null) {
      return;
    }

    // Ensure channel is subscribed (idempotent; normally already done on login).
    pusherService.connect(Number(accountId), Number(userId));
  }, [accountId, userId]);

  const endSession = useCallback(() => {
    stopWaiting();
  }, [stopWaiting]);

  const attachWaiting = useCallback(
    ({ orderId, uuid, onProgress, onReady, onFailed }) => {
      stopWaiting();

      let unsubDownload = null;
      let unsubProgress = null;
      let readyTriggered = false;

      const matchesContext = (payload) =>
        Number(payload?.orderId) === Number(orderId) &&
        String(payload?.uuid) === String(uuid)

      const cleanup = () => {
        unsubDownload?.();
        unsubProgress?.();
        unsubDownload = null;
        unsubProgress = null;
      };

      const triggerReady = () => {
        if (readyTriggered) {
          return;
        }

        readyTriggered = true;
        cleanup();
        waitingCleanupRef.current = null;
        onReady?.();
      };

      const triggerFailed = (message) => {
        if (readyTriggered) {
          return;
        }

        readyTriggered = true;
        cleanup();
        waitingCleanupRef.current = null;
        onFailed?.(message);
      };

      const handleProgress = (payload) => {
        if (readyTriggered || !matchesContext(payload)) {
          return;
        }

        onProgress?.(payload);

        if (payload?.step === "FAILED") {
          triggerFailed(
            payload?.message ||
              payload?.error ||
              "Failed to generate encrypted declaration files.",
          );
        }
      };

      const handleDownloadCompleted = (payload) => {
        if (readyTriggered || !matchesContext(payload)) {
          return;
        }

        if (payload?.status === "READY") {
          triggerReady();
        }
      };

      unsubDownload = pusherService.subscribe(
        "download-completed",
        handleDownloadCompleted,
      );
      unsubProgress = pusherService.subscribe(
        "encrypted-declaration-progress",
        handleProgress,
      );

      waitingCleanupRef.current = cleanup;
      return cleanup;
    },
    [stopWaiting],
  );

  useEffect(() => () => stopWaiting(), [stopWaiting]);

  return useMemo(
    () => ({
      startSession,
      endSession,
      attachWaiting,
      stopWaiting,
    }),
    [attachWaiting, endSession, startSession, stopWaiting],
  );
}

export default useDownloadRealtimeSession;
